import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { TIDS } from "@/constants/testIds";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  Globe2, CheckCircle2, XCircle, Clock, Copy, Trash2, ShieldCheck, RefreshCw, Lock, Home as HomeIcon,
} from "lucide-react";

const STATUS_COLOR = {
  pending: "text-[#8A6D3B] bg-[#FFF3CD] border-[#F1DFB5]",
  verified: "text-[#1E8E3E] bg-[#E7F5EC] border-[#B7E1C0]",
  failed: "text-[#B00020] bg-[#FDE8EA] border-[#F3B7BE]",
};

const StatusPill = ({ status }) => {
  const Icon = status === "verified" ? CheckCircle2 : status === "failed" ? XCircle : Clock;
  return (
    <span className={"inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border " + (STATUS_COLOR[status] || STATUS_COLOR.pending)}>
      <Icon className="w-3.5 h-3.5" strokeWidth={2} /> {status}
    </span>
  );
};

// Live status from the Approximated proxy. Colour-coded so the customer knows
// exactly what to do next (usually: fix DNS or wait for SSL to issue).
const APX_LABEL = {
  DNS_INCORRECT:  { text: "DNS incorrect",   cls: "text-[#B00020] bg-[#FDE8EA] border-[#F3B7BE]" },
  DNS_CHECKING:   { text: "Checking DNS",    cls: "text-[#8A6D3B] bg-[#FFF3CD] border-[#F1DFB5]" },
  DNS_PENDING:    { text: "DNS pending",     cls: "text-[#8A6D3B] bg-[#FFF3CD] border-[#F1DFB5]" },
  DNS_POINTED_AT_APX:  { text: "DNS OK · issuing SSL", cls: "text-[#0B5CFF] bg-[#E6EEFF] border-[#B7CBF5]" },
  SSL_PENDING:    { text: "Issuing SSL",     cls: "text-[#0B5CFF] bg-[#E6EEFF] border-[#B7CBF5]" },
  SSL_ACTIVE:     { text: "Live · SSL",      cls: "text-[#1E8E3E] bg-[#E7F5EC] border-[#B7E1C0]" },
};

const ApxStatusPill = ({ status, hasSsl, isResolving, onRefresh }) => {
  const key = hasSsl ? "SSL_ACTIVE" : (status || "DNS_CHECKING");
  const cfg = APX_LABEL[key] || { text: status || "unknown", cls: "text-[#4A4D57] bg-[#F1F3F7] border-[#E4E6EC]" };
  return (
    <button
      type="button"
      onClick={onRefresh}
      className={"inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border cursor-pointer hover:opacity-80 " + cfg.cls}
      title="Live status from Approximated · click to refresh"
    >
      <Lock className="w-3.5 h-3.5" strokeWidth={2} />
      Proxy: {cfg.text}
      {isResolving === false && !hasSsl && <span className="opacity-60">· not resolving</span>}
    </button>
  );
};

const Copyable = ({ label, value }) => (
  <div className="flex items-start justify-between gap-3 py-2.5 border-b border-[#E4E6EC] last:border-b-0">
    <div className="min-w-0">
      <div className="vp-label" style={{ color: "#747680" }}>{label}</div>
      <div className="font-mono text-sm text-[#0B0D17] break-all mt-1">{value}</div>
    </div>
    <button
      onClick={() => { navigator.clipboard.writeText(value); toast.success(`${label} copied`); }}
      className="shrink-0 p-1.5 rounded-md hover:bg-[#F1F3F7] text-[#4A4D57]"
      title="Copy"
    >
      <Copy className="w-4 h-4" strokeWidth={1.5} />
    </button>
  </div>
);

function DnsInstructionsCard({ instructions }) {
  const isApex = instructions.is_apex;
  return (
    <div className="vp-card p-5 space-y-1">
      {isApex ? (
        <>
          <div className="font-display font-semibold text-sm mb-1">1) Add these A records (apex domain)</div>
          <div className="text-xs text-[#B45309] bg-[#FEF3C7] border border-[#FDE68A] rounded-md p-2 mb-3">
            <b>ministryofcreator.com</b> is a root/apex domain. DNS spec forbids CNAME at the root — you must use A records.
          </div>
          {(instructions.a_records?.values || []).map((ip, i) => (
            <Copyable key={ip} label={`A record ${i + 1}`} value={ip} />
          ))}
          <Copyable label="Host / Name" value={instructions.a_records.host} />
          <Copyable label="TTL" value={String(instructions.a_records.ttl)} />
          <div className="text-xs text-[#747680] pt-2">{instructions.a_records.note}</div>
        </>
      ) : (
        <>
          <div className="font-display font-semibold text-sm mb-2">1) Add this CNAME record</div>
          <Copyable label="Type" value={instructions.cname.type} />
          <Copyable label="Host / Name" value={instructions.cname.host} />
          <Copyable label="Value / Target" value={instructions.cname.value} />
          <Copyable label="TTL" value={String(instructions.cname.ttl)} />
          <div className="text-xs text-[#747680] pt-2">{instructions.cname.note}</div>
          {(instructions.a_records?.values || []).length > 0 && (
            <details className="mt-4 pt-3 border-t border-[#E4E6EC]">
              <summary className="cursor-pointer text-xs text-[#4A4D57] font-medium">
                Or use A records instead (advanced)
              </summary>
              <div className="mt-2 space-y-1">
                {instructions.a_records.values.map((ip, i) => (
                  <Copyable key={ip} label={`A record ${i + 1}`} value={ip} />
                ))}
              </div>
            </details>
          )}
        </>
      )}

      <div className="font-display font-semibold text-sm mt-5 mb-2">2) Add this TXT record (ownership proof)</div>
      <Copyable label="Type" value={instructions.txt.type} />
      <Copyable label="Host / Name" value={instructions.txt.host} />
      <Copyable label="Value" value={instructions.txt.value} />
      <Copyable label="TTL" value={String(instructions.txt.ttl)} />
      <div className="text-xs text-[#747680] pt-2">{instructions.txt.note}</div>
    </div>
  );
}


function VerificationDetail({ lastResult }) {
  if (!lastResult) return null;
  const routeOk = lastResult.routing_ok;
  const txtOk = lastResult.txt_ok;
  return (
    <div className="mt-3 rounded-lg border border-[#E4E6EC] bg-[#F6F7FA] p-3 space-y-2 text-xs font-mono">
      <div className="font-sans text-[11px] uppercase tracking-wider text-[#747680] font-semibold">Last DNS check</div>
      <div className="flex gap-2 flex-wrap">
        <span className={routeOk ? "text-[#1E8E3E]" : "text-[#B00020]"}>
          {routeOk ? "✓" : "✗"} Routing (CNAME or A)
        </span>
        <span className={txtOk ? "text-[#1E8E3E]" : "text-[#B00020]"}>
          {txtOk ? "✓" : "✗"} TXT ownership
        </span>
      </div>
      <div className="pt-1 border-t border-[#E4E6EC] space-y-1 break-all">
        <div><b className="font-sans">Expected CNAME:</b> {lastResult.expected?.cname_target}</div>
        <div><b className="font-sans">Expected A records:</b> {(lastResult.expected?.a_records || []).join(", ")}</div>
        <div><b className="font-sans">Found CNAME:</b> {(lastResult.cname_seen || []).join(", ") || "—"}</div>
        <div><b className="font-sans">Found A:</b> {(lastResult.a_seen || []).join(", ") || "—"}</div>
        <div><b className="font-sans">Expected TXT host:</b> {lastResult.expected?.txt_host}</div>
        <div><b className="font-sans">Expected TXT value:</b> {lastResult.expected?.txt_value}</div>
        <div><b className="font-sans">Found TXT:</b> {(lastResult.txt_seen || []).join(" | ") || "—"}</div>
      </div>
    </div>
  );
}

export default function Domains() {
  const [plan, setPlan] = useState(null);
  const [domains, setDomains] = useState([]);
  const [pages, setPages] = useState([]);
  const [newDomain, setNewDomain] = useState("");
  const [adding, setAdding] = useState(false);
  const [verifying, setVerifying] = useState(null);
  const [openInstructions, setOpenInstructions] = useState(null);
  const [primaryBusy, setPrimaryBusy] = useState(null);

  const load = async () => {
    try {
      const [{ data: p }, { data: d }, { data: pg }] = await Promise.all([
        api.get("/plans/current"),
        api.get("/domains"),
        api.get("/landing-pages"),
      ]);
      setPlan(p);
      setDomains(d);
      setPages(pg.filter((x) => x.published));
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  useEffect(() => { load(); }, []);

  const setPrimary = async (domainId, pageId) => {
    setPrimaryBusy(domainId);
    try {
      const { data } = await api.patch(`/domains/${domainId}/primary-page`, { primary_page_id: pageId || null });
      setDomains((prev) => prev.map((d) => (d.id === domainId ? data : d)));
      toast.success(pageId ? "Primary landing page linked" : "Primary landing page cleared");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setPrimaryBusy(null); }
  };

  const add = async (e) => {
    e.preventDefault();
    if (!newDomain.trim()) return;
    setAdding(true);
    try {
      const { data } = await api.post("/domains", { domain: newDomain.trim() });
      setDomains((prev) => [data, ...prev]);
      setOpenInstructions(data);
      setNewDomain("");
      toast.success("Domain added. Set DNS records to verify.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setAdding(false); }
  };

  const verify = async (id) => {
    setVerifying(id);
    try {
      const { data } = await api.post(`/domains/${id}/verify`);
      setDomains((prev) => prev.map((d) => (d.id === id ? data : d)));
      if (data.status === "verified") toast.success("Domain verified. SSL is provisioning.");
      else toast.error("DNS records not detected yet. Try again in a few minutes.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setVerifying(null); }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this custom domain? Landing pages served on it will stop working.")) return;
    try {
      await api.delete(`/domains/${id}`);
      setDomains((prev) => prev.filter((d) => d.id !== id));
      toast.success("Domain removed");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const [reconnecting, setReconnecting] = useState(null);
  const reconnectProxy = async (id) => {
    setReconnecting(id);
    try {
      const { data } = await api.post(`/domains/${id}/apx/reconcile`);
      setDomains((prev) => prev.map((d) => (d.id === id ? data : d)));
      toast.success(`Proxy registered · ${data.apx_status || "pending"}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setReconnecting(null); }
  };

  const refreshApxStatus = async (id) => {
    try {
      const { data } = await api.get(`/domains/${id}/apx/status`);
      if (data.configured === false) return;
      setDomains((prev) => prev.map((d) => (
        d.id === id ? { ...d, apx_status: data.status, apx_has_ssl: data.has_ssl, apx_is_resolving: data.is_resolving, apx_vhost_id: data.id } : d
      )));
      toast.success(`Proxy status · ${data.status || "unknown"}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  if (!plan) return <div className="text-[#4A4D57]">Loading…</div>;

  const canAddDomain = plan.features.custom_domains;
  const limitReached = canAddDomain && domains.length >= plan.features.max_custom_domains;

  return (
    <div className="space-y-8 vp-fade-up max-w-4xl">
      <div>
        <div className="vp-label">White-label</div>
        <h1 className="font-display text-3xl font-bold mt-1">Custom domains</h1>
        <p className="text-[#4A4D57] mt-2 max-w-2xl">
          Serve your webinar landing pages on your own domain (e.g. <span className="font-mono">webinar.yoursite.com</span>). Videoepreneur branding stays hidden — attendees see only you.
        </p>
      </div>

      {/* Plan gate */}
      {!canAddDomain && (
        <div className="vp-glass p-6 border-[#0B5CFF]/40">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#0B5CFF]/10 flex items-center justify-center shrink-0">
              <Lock className="w-5 h-5 text-[#0B5CFF]" strokeWidth={1.75} />
            </div>
            <div className="flex-1">
              <div className="font-display font-bold text-lg">Custom domains are a Pro feature</div>
              <p className="text-sm text-[#4A4D57] mt-1">
                You're on the <span className="font-semibold text-[#0B0D17]">{plan.features.label}</span> plan. Upgrade to Pro or Business to point your own domain at Videoepreneur.
              </p>
              <Link to="/app/plans" className="vp-btn-primary text-sm mt-4 inline-block" data-testid={TIDS.domainUpgradeCta}>
                View plans
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Add new */}
      {canAddDomain && (
        <form onSubmit={add} className="vp-glass p-5">
          <div className="vp-label mb-2">Add a new domain</div>
          <div className="flex flex-col sm:flex-row gap-3">
            <input
              className="vp-input flex-1"
              placeholder="webinar.yoursite.com"
              value={newDomain}
              onChange={(e) => setNewDomain(e.target.value)}
              disabled={limitReached}
              data-testid={TIDS.domainAddInput}
            />
            <button
              type="submit"
              disabled={adding || limitReached || !newDomain.trim()}
              className="vp-btn-primary sm:w-auto"
              data-testid={TIDS.domainAddBtn}
            >
              {adding ? "Adding…" : "Add domain"}
            </button>
          </div>
          <div className="text-xs text-[#747680] mt-2">
            {limitReached
              ? `You've reached your plan's limit of ${plan.features.max_custom_domains} domain(s). Upgrade for more.`
              : `Plan ${plan.features.label} · up to ${plan.features.max_custom_domains} domain(s).`}
          </div>
        </form>
      )}

      {/* Domains list */}
      <div className="space-y-4">
        {domains.length === 0 && canAddDomain && (
          <div className="vp-card p-8 text-center text-[#4A4D57]">
            <Globe2 className="w-8 h-8 text-[#0B5CFF] mx-auto mb-3" strokeWidth={1.5} />
            No custom domains yet. Add your first one above.
          </div>
        )}
        {domains.map((d) => (
          <div key={d.id} className="vp-card vp-card-hover p-5" data-testid={TIDS.domainRow}>
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <Globe2 className="w-5 h-5 text-[#0B5CFF]" strokeWidth={1.5} />
                  <div className="font-display font-bold text-lg break-all">{d.domain}</div>
                  <StatusPill status={d.status} />
                  {d.status === "verified" && d.ssl_status === "active" && (
                    <span className="inline-flex items-center gap-1 text-xs text-[#1E8E3E] font-semibold">
                      <ShieldCheck className="w-3.5 h-3.5" strokeWidth={2} /> SSL active
                    </span>
                  )}
                </div>
                <div className="text-xs text-[#747680] mt-1.5">
                  Added {new Date(d.created_at).toLocaleString("en-IN")}
                  {d.last_verified_at && <> · Last checked {new Date(d.last_verified_at).toLocaleString("en-IN")}</>}
                </div>
                {d.status === "failed" && d.last_verification_result && (
                  <VerificationDetail lastResult={d.last_verification_result} />
                )}
                {d.status === "verified" && (
                  <div className="mt-3 flex items-center gap-2 flex-wrap">
                    <HomeIcon className="w-3.5 h-3.5 text-[#0B5CFF]" strokeWidth={1.75} />
                    <label className="vp-label" style={{ fontSize: "0.65rem" }}>Home page shows</label>
                    <select
                      value={d.primary_page_id || ""}
                      onChange={(e) => setPrimary(d.id, e.target.value || null)}
                      disabled={primaryBusy === d.id}
                      className="vp-input text-xs py-1.5 px-2 max-w-xs"
                      data-testid={"domain-primary-select-" + d.id}
                    >
                      <option value="">Default marketing site</option>
                      {pages.map((p) => (
                        <option key={p.id} value={p.id}>{p.title}</option>
                      ))}
                    </select>
                    {pages.length === 0 && (
                      <span className="text-xs text-[#747680]">— publish a landing page first to link it</span>
                    )}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                {d.apx_status && (
                  <ApxStatusPill
                    status={d.apx_status}
                    hasSsl={d.apx_has_ssl}
                    isResolving={d.apx_is_resolving}
                    onRefresh={() => refreshApxStatus(d.id)}
                  />
                )}
                <button
                  onClick={() => setOpenInstructions(d)}
                  className="vp-btn-ghost text-sm"
                  title="Show DNS instructions"
                >
                  DNS records
                </button>
                <button
                  onClick={() => reconnectProxy(d.id)}
                  disabled={reconnecting === d.id}
                  className="vp-btn-ghost text-sm inline-flex items-center gap-1.5"
                  title="Re-register on Approximated proxy"
                  data-testid={`domain-reconnect-${d.id}`}
                >
                  <RefreshCw className={"w-4 h-4 " + (reconnecting === d.id ? "animate-spin" : "")} strokeWidth={1.5} />
                  {reconnecting === d.id ? "Reconnecting…" : "Reconnect proxy"}
                </button>
                <button
                  onClick={() => verify(d.id)}
                  disabled={verifying === d.id}
                  className="vp-btn-primary text-sm inline-flex items-center gap-2"
                  data-testid={TIDS.domainVerifyBtn}
                >
                  <RefreshCw className={"w-4 h-4 " + (verifying === d.id ? "animate-spin" : "")} strokeWidth={1.5} />
                  {verifying === d.id ? "Checking…" : "Verify"}
                </button>
                <button
                  onClick={() => remove(d.id)}
                  className="p-2 rounded-lg text-[#4A4D57] hover:text-[#B00020] hover:bg-[#FDE8EA] transition-colors"
                  data-testid={TIDS.domainDeleteBtn}
                  title="Remove"
                >
                  <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* DNS Instructions modal */}
      <Dialog open={!!openInstructions} onOpenChange={(v) => !v && setOpenInstructions(null)}>
        <DialogContent className="bg-[#FFFFFF] border-[#E4E6EC] text-[#0B0D17] max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">DNS setup — {openInstructions?.domain}</DialogTitle>
            <DialogDescription className="text-[#4A4D57]">
              Log into your DNS provider (GoDaddy, Cloudflare, Namecheap, Route53) and add the two records below. Then come back and click <span className="font-semibold text-[#0B0D17]">Verify</span>.
            </DialogDescription>
          </DialogHeader>
          {openInstructions && <DnsInstructionsCard instructions={openInstructions.dns_instructions} />}
          <div className="text-xs text-[#747680]">DNS propagation can take a few minutes to several hours.</div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
